import type * as CSS from 'csstype';
import React from 'react';
import styled from 'styled-components';

import { useTheme } from '../../state/theme-state';
import { Stack } from '../stack/stack';

type Props = {
  children: React.ReactNode;
  width: CSS.Properties['width'];
  overrideThemeBackgroundImageUrl?: string;
  hideLogotype?: boolean;
};

const FullPageBox = ({ hideLogotype = false, ...props }: Props) => {
  const { getFullPageBoxBackground, getFullPageBoxBackgroundSize, getLogoTypeUrl } = useTheme();

  return (
    <Component>
      <Background
        overrideThemeBackgroundImageUrl={props.overrideThemeBackgroundImageUrl}
        themeBackground={getFullPageBoxBackground()}
        themeBackgroundSize={getFullPageBoxBackgroundSize()}
      />
      <Box width={props.width}>
        <Stack direction="column" spacing={2}>
          {!hideLogotype && <Logotype logotypeUrl={getLogoTypeUrl()} />}
          <div>{props.children}</div>
        </Stack>
      </Box>
    </Component>
  );
};

const Component = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 100vh;
  padding: 1.5em;
  box-sizing: border-box;
  overflow: hidden;
`;

const Background = styled.div<{
  overrideThemeBackgroundImageUrl?: string;
  themeBackground: string;
  themeBackgroundSize: string;
}>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  background: ${(props) =>
    props.overrideThemeBackgroundImageUrl
      ? `url(${props.overrideThemeBackgroundImageUrl}) center center no-repeat`
      : props.themeBackground};
  background-size: ${(props) =>
    props.overrideThemeBackgroundImageUrl ? 'cover' : props.themeBackgroundSize};
`;

const Logotype = styled.div<{ logotypeUrl: string }>`
  width: 100%;
  height: 3em;
  background: url(${(props) => props.logotypeUrl}) center no-repeat;
  background-size: contain;
`;

const Box = styled.div<Pick<Props, 'width'>>`
  width: ${(props) => props.width};
  padding: 1.3em;
  background-color: #fff;
  border-radius: 0.5em;
  box-shadow:
    0 3px 6px rgba(0, 0, 0, 0.16),
    0 3px 6px rgba(0, 0, 0, 0.23);
  z-index: 1;
  font-family: 'Montserrat', sans-serif;
  color: #000;
`;

export { FullPageBox };
